import api, { unwrap } from '../finance/api/client';

/**
 * Tasks API Service adhering strictly to backend tasks_api_reference.md
 * Base Path: /api/tasks
 */

export const tasksApi = {
  // 1. Create Task
  createTask: async (taskData) => {
    // taskData: { title, description, taskStatus, priority, deadLine, projectId, username }
    const res = await api.post('/tasks/create', taskData);
    return unwrap(res);
  },

  // 2. Get My Assigned Tasks
  getMyTasks: async (params = {}) => {
    const res = await api.get('/tasks/my-tasks', { params });
    return unwrap(res);
  },

  // 3. Get Tasks Created By Me
  getCreatedByMeTasks: async (params = {}) => {
    const res = await api.get('/tasks/created-by-me', { params });
    return unwrap(res);
  },

  // 4. Get Team Tasks (Role-aware: Superadmin gets all org, Admin gets team/dept + copyTo, User gets copyTo tasks)
  getTeamTasks: async (params = {}) => {
    const res = await api.get('/tasks/team-tasks', { params });
    return unwrap(res);
  },

  // 4.1 Get Tasks Copied to Current User
  getCopiedToMeTasks: async (params = {}) => {
    const res = await api.get('/tasks/copied-to-me', { params });
    return unwrap(res);
  },

  // 5. Get Assignees (Role-unrestricted: all authenticated users can get active assignees)
  getAssignees: async () => {
    const res = await api.get('/tasks/assignees');
    return unwrap(res);
  },

  // 5.1 Get Projects for task linking
  getProjects: async () => {
    try {
      const res = await api.get('/project/getAllProjects');
      return unwrap(res);
    } catch (e) {
      console.warn('Could not fetch projects:', e);
      return [];
    }
  },

  // 5.2 Get Products for task linking
  getProducts: async () => {
    try {
      const res = await api.get('/product/getAllProducts');
      return unwrap(res);
    } catch (e) {
      console.warn('Could not fetch products:', e);
      return [];
    }
  },

  // 5.3 Single endpoint for Task Form context (products, cascaded projects, and product/project assignees)
  getTaskFormContext: async (productId, projectId) => {
    try {
      const params = {};
      if (productId) params.productId = productId;
      if (projectId) params.projectId = projectId;
      const res = await api.get('/tasks/form-context', { params });
      return unwrap(res);
    } catch (e) {
      console.warn('Could not fetch task form context:', e);
      return { products: [], projects: [], assignees: [] };
    }
  },

  // 6. Get Project Tasks
  getProjectTasks: async (projectId, params = {}) => {
    const res = await api.get(`/tasks/project/${projectId}`, { params });
    return unwrap(res);
  },

  // 6.1 Get Product Tasks
  getProductTasks: async (productId, params = {}) => {
    const res = await api.get(`/tasks/product/${productId}`, { params });
    return unwrap(res);
  },

  // 6.2 Get Single Task by ID
  getTaskById: async (taskId) => {
    const id = Number(taskId) || taskId;
    const res = await api.get(`/tasks/${id}`);
    return unwrap(res);
  },

  // 7. Admin — Get All Tasks
  getAllTasks: async (params = {}) => {
    const res = await api.get('/tasks/all', { params });
    return unwrap(res);
  },

  // 8. Update Task
  updateTask: async (taskId, taskData) => {
    const res = await api.put(`/tasks/${taskId}`, taskData);
    return unwrap(res);
  },

  // 9. Update Task Status
  updateTaskStatus: async (taskId, status) => {
    const res = await api.put(`/tasks/${taskId}/status`, null, {
      params: { status }
    });
    return unwrap(res);
  },

  // 10. Assign Task
  assignTask: async (taskId, username) => {
    const res = await api.post('/tasks/assign', { taskId, username });
    return unwrap(res);
  },

  // 11. Delete Task (DELETE /api/tasks/{taskId})
  deleteTask: async (taskId) => {
    const id = Number(taskId) || taskId;
    const res = await api.delete(`/tasks/${id}`);
    return unwrap(res);
  },

  // 12. Upload Attachment
  uploadAttachment: async (taskId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/tasks/${taskId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return unwrap(res);
  },

  // 12b. Delete Attachment
  deleteAttachment: async (taskId, attachmentId) => {
    const res = await api.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
    return unwrap(res);
  },

  // 13. Create Subtask
  createSubtask: async (subtaskData) => {
    const res = await api.post('/tasks/subtask/create', subtaskData);
    return unwrap(res);
  },

  // 14. Get Subtasks
  getSubtasks: async (taskId) => {
    const res = await api.get(`/tasks/subtask/${taskId}`);
    return unwrap(res);
  },

  // 15. Delete Subtask
  deleteSubtask: async (subTaskId) => {
    const res = await api.delete(`/tasks/subtask/${subTaskId}`);
    return unwrap(res);
  },

  // 15b. Update Subtask Status (POST /api/tasks/subtask/create)
  updateSubtaskStatus: async (subTaskId, status, parentTaskId, subTaskTitle) => {
    const res = await api.post('/tasks/subtask/create', {
      subTaskId: Number(subTaskId),
      taskId: Number(parentTaskId),
      subTaskTitle: subTaskTitle || 'Subtask',
      subTaskStatus: status
    });
    return unwrap(res);
  },

  // 16. Create Comment
  createComment: async (taskId, comment) => {
    const res = await api.post('/tasks/comment/newcomment', {
      taskId: Number(taskId),
      comment,
      description: comment,
      text: comment,
      content: comment
    });
    return unwrap(res);
  },

  // 17. Get Comments
  getComments: async (taskId) => {
    const res = await api.get(`/tasks/comment/getComments`, {
      params: { taskId }
    });
    return unwrap(res);
  },

  // 18. Delete Comment
  deleteComment: async (commentId) => {
    const res = await api.delete(`/tasks/comment/${commentId}`);
    return unwrap(res);
  }
};

/**
 * Check if the given user (or currently logged-in user) can edit the task.
 * Rule: Editing a task is ONLY permitted for the assignee and the creator of the task.
 *
 * @param {Object} task The task object
 * @param {string} [currentUsername] Optional username string (defaults to localStorage.getItem('username'))
 * @returns {boolean} True if user is either assignee or creator
 */
export const canEditTask = (task, currentUsername = null) => {
  if (!task) return false;
  const username = (currentUsername || (typeof localStorage !== 'undefined' ? localStorage.getItem('username') : '') || '').trim().toLowerCase();
  if (!username) return false;

  const isAssignee = Boolean(
    (task.username && String(task.username).trim().toLowerCase() === username) ||
    (task.user?.username && String(task.user.username).trim().toLowerCase() === username) ||
    (task.assignee && String(task.assignee).trim().toLowerCase() === username) ||
    (task.assigneeName && String(task.assigneeName).trim().toLowerCase() === username)
  );

  const isCreator = Boolean(
    (task.createdBy && String(task.createdBy).trim().toLowerCase() === username) ||
    (task.createdByName && String(task.createdByName).trim().toLowerCase() === username) ||
    (task.createdByUser?.username && String(task.createdByUser.username).trim().toLowerCase() === username)
  );

  return isAssignee || isCreator;
};


/**
 * Checks whether the current user is authorized to update the status of a task.
 * 
 * Rules:
 * 1. If a task is COMPLETED, status change is strictly restricted to:
 *    - The task Creator
 *    - Superadmin / Admin
 *    (Regular assignees or general team members CANNOT change status of a completed task)
 * 2. If a task is NOT completed (TO_DO, IN_PROGRESS, BLOCKED):
 *    - Assignee, Creator, or Admin/Superadmin can update the status.
 */
export const canChangeTaskStatus = (task, currentUsername = null, role = null) => {
  if (!task) return false;
  const username = (currentUsername || (typeof localStorage !== 'undefined' ? localStorage.getItem('username') : '') || '').trim().toLowerCase();
  if (!username) return false;

  const userRole = (role || (typeof localStorage !== 'undefined' ? localStorage.getItem('role') : '') || '').toUpperCase();
  const isAdmin = userRole.includes('ADMIN') || userRole.includes('SUPERADMIN') || userRole.includes('TESTER');

  const taskCreatorNorm = (task.createdBy || task.createdByName || task.createdByUser?.username || '').trim().toLowerCase();
  const isCreator = Boolean(taskCreatorNorm && taskCreatorNorm === username);

  // If task is COMPLETED, ONLY Creator or Admin/SuperAdmin can change/reopen its status!
  if (task.taskStatus === 'COMPLETED') {
    return isCreator || isAdmin;
  }

  // When task is active, Assignee, Creator, or Admin can update status
  const isAssignee = Boolean(
    (task.username && String(task.username).trim().toLowerCase() === username) ||
    (task.user?.username && String(task.user.username).trim().toLowerCase() === username) ||
    (task.assignee && String(task.assignee).trim().toLowerCase() === username) ||
    (task.assigneeName && String(task.assigneeName).trim().toLowerCase() === username)
  );

  return isAssignee || isCreator || isAdmin;
};

export default tasksApi;
